import { z } from 'zod';
import { BasemapTweakSchema } from '../../app/map/baseMap';
import { ExpressionSpecificationSchema } from '../../app/map/mapbox';
import { TipSchema } from '../../app/references/tip';
import { I18nBlockContentSchema } from '../../app/ui/blockContent';
import { ImageAssetSchema } from '../../app/ui/imageAsset';
import { I18nStringSchema } from '../../core/i18n';
/*
  Enums/Unions
*/
const LayerTypeSchema = z.enum([
    'metric (percentile)',
    'metric (units)',
    'feature',
    'boundary'
]);
const DataLayerStyleSchema = z.enum([
    'Raster',
    'Points',
    'Lines',
    'Polygons',
    'Polygons (symbolized)',
    'Animated',
    'Animated (particles)'
]);
const RadiusTypeSchema = z.enum(['$high-density', '$low-density']);
export const ExcludeLayerSchema = z.enum(['stroke', 'label', 'fill']);
/*
  Layer Value sub-types
*/
const PointIconSchema = z.object({
    url: z.string(),
    assetId: z.string()
});
const StyleConfigSchema = z.object({
    color: z.string(),
    exclude: z.array(ExcludeLayerSchema),
    // Points
    radius: RadiusTypeSchema.optional(),
    pointIcon: PointIconSchema.optional(),
    // Fills
    opacity: z.number().min(0).max(1).optional(),
    // Metric layers
    targetAttribute: z.string().optional(),
    maxValue: z.number().optional(),
    minValue: z.number().optional(),
    // Polygons (Symbolized)
    image: z.string().optional(),
    modulator: z.tuple([z.literal('get'), z.string()]).optional(), // matches ['get', 'someStr']
    modulus: z.number().min(1).max(50).optional()
});
const LayerConfigSchema = z.object({
    minzoom: z.number().min(0).max(22).optional(),
    maxzoom: z.number().min(0).max(22).optional(),
    filter: ExpressionSpecificationSchema.optional()
});
export const DataSourceSchema = z.object({
    _id: z.string(),
    name: I18nStringSchema,
    sourceUrl: z.string(),
    sourceName: z.string(),
    lastUpdatedYear: z.number()
});
/*
  Layer Values, Map Layers
*/
export const LayerValueSchema = z.object({
    key: z.string(),
    name: I18nStringSchema,
    tilesetUrl: z
        .string()
        .regex(/^mapbox:\/\/\w+\..+$/) // mapbox://orgname.tileset-name
        .optional(), // undefined for Animated layers
    tilesetLayer: z.string().optional(), // undefined for Animated and Raster layers
    promoteId: z.string().optional(),
    interactive: z.boolean(),
    headings: z.array(z.string()),
    muted: z.array(z.string()),
    style: DataLayerStyleSchema,
    styleConfig: StyleConfigSchema,
    layerConfig: LayerConfigSchema,
    labelConfig: LayerConfigSchema
});
export const LayerValueReferenceSchema = LayerValueSchema.extend({
    dataSources: z.array(DataSourceSchema).min(1)
});
const MapLayersSchema = z.object({
    // multiple mapbox layers for a single tileset ('fills' and 'labels', for example)
    mapboxLayers: z.array(z.any()).min(1), // see @lifk/types.ts for notes
    // multiple layerValues that share the same tileset+layer and minzooms
    layerValues: z.array(LayerValueSchema).min(1)
});
const MapLayersReferenceSchema = MapLayersSchema.extend({
    layerValues: z.array(LayerValueReferenceSchema).min(1)
});
/*
  Layer Packs
*/
export const LayerPackSchema = z.object({
    _id: z.string(),
    mapScreenshot: ImageAssetSchema,
    description: I18nStringSchema,
    /*
      To avoid infinite/excessive nesting, this interface only
        uses simple ref ids instead of entire objects for associations.
      This allows Layer Packs to be safely nested
        within Data Layers and Effects, and referenced from either.
    */
    effect: z.string(),
    dataLayers: z.array(z.string()).min(1)
});
/*
  Data Layers
*/
export const BaseDataLayerSchema = z.object({
    key: z.string(),
    name: I18nStringSchema,
    layerType: LayerTypeSchema,
    isGradient: z.boolean(),
    showGreaterThan: z.boolean(),
    emptyIsNoData: z.boolean(),
    basemapTweaks: z.array(BasemapTweakSchema),
    mapScreenshot: ImageAssetSchema,
    shortDescription: I18nStringSchema,
    lastUpdatedYear: z.number(),
    mapLayers: z.array(MapLayersSchema).min(1) // Added dynamically
});
export const BaseDataLayerReferenceSchema = BaseDataLayerSchema.extend({
    // use expanded mapLayers including Data Sources
    mapLayers: z.array(MapLayersReferenceSchema).min(1),
    longDescription: I18nStringSchema,
    methodology: I18nBlockContentSchema,
    tips: z.array(TipSchema)
});
//# sourceMappingURL=layers.js.map